import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

/**
 * Auth0 reports a failed /authorize hop by redirecting back to the callback URL with
 * `error` + `error_description` and no `code`/`state`. auth0-react ignores that shape
 * (its callback detection requires `state`), so without this wall the app falls through
 * to AuthWall and bounces the visitor to /identifier with no explanation — the DM-657
 * symptom for invitation links that were deleted or expired.
 *
 * Auth0 returns the same signal for both of those cases
 * (`invalid_request` / "invitation not found or already used"), so one message covers them.
 */
export const AuthErrorWall: FC<PropsWithChildren> = ({ children }) => {
  const tErrors = useTranslations('errors');
  const [error, setError] = useState<{ isInvitation: boolean } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const description = params.get('error_description');
    // `code`/`state` belong to a real Auth0 callback — leave those to the SDK.
    if (!params.get('error') || !description || params.get('code') || params.get('state')) {
      return;
    }
    setError({ isInvitation: /invitation/i.test(description) });
  }, []);

  if (!error) {
    return children;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-black" data-cy="auth-error">
      <div className="w-full max-w-100 rounded-md bg-white px-12 py-10 shadow-[0_2px_4px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6 flex justify-center">
          <Image src="/lkbks-logo.png" alt="Lkbks" width={52} height={57} className="object-contain" />
        </div>
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <h1 className="text-2xl leading-tight text-[#1c1c1e]">
            {error.isInvitation ? tErrors('invitationInvalid') : tErrors('signInFailed')}
          </h1>
          <p className="text-sm text-black">
            {error.isInvitation ? tErrors('invitationInvalidDescription') : tErrors('signInFailedDescription')}
          </p>
        </div>
        {/* This card, like `pages/identifier.tsx` it links back to, always renders light —
            it has no `dark:` classes of its own — but `Button`'s default variant swaps to
            dark-mode colors whenever the app's `dark` class is set, which reads as
            near-black text on a near-black button here. Pin the button's colors so they
            match this card's fixed light styling instead, the same way identifier.tsx's
            submit button pins its own colors. */}
        <Button
          asChild
          className="h-12 w-full bg-neutral-900! text-white! hover:bg-neutral-900/90!"
          data-cy="auth-error-sign-in"
        >
          {/* Full-page navigation on purpose: a client-side route change would keep Auth0's
              `error`/`error_description` params on the URL and re-render this wall. */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a href="/identifier">{tErrors('backToSignIn')}</a>
        </Button>
      </div>
    </div>
  );
};
